"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Chip } from "@heroui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  SparklesIcon,
  CodeIcon,
  SecurityCheckIcon,
  GitBranchIcon,
} from "@hugeicons/core-free-icons";

// ─── Data & Types ────────────────────────────────────────────────────────────

const WhyChoseUsArray = [
  {
    key: "engineering",
    title: "AI-Powered Engineering",
    icon: SparklesIcon,
    accentColor: "from-emerald-500/10 to-teal-500/10 dark:from-emerald-500/20 dark:to-teal-500/5",
    points: [
      {
        subKey: "copilots",
        subTitle: "AI Copilots",
        point: "AI-Assisted Development Workflows",
        description:
          "Our developers leverage advanced AI copilots (Claude, GPT-4, Cursor) to automate boilerplate setup, speed up logic implementation, and deliver complex features up to 3x faster without compromising code stability.",
      },
      {
        subKey: "auditing",
        subTitle: "Auto Auditing",
        point: "Continuous LLM-Powered Code Review",
        description:
          "Every pull request is automatically analyzed by custom-trained static analysis agents and LLM code reviewers to spot performance regressions, security issues, and compliance gaps before human review.",
      },
      {
        subKey: "e2e",
        subTitle: "Automated E2E",
        point: "Autonomous E2E Test Suite Generation",
        description:
          "We use autonomous agents to write and maintain comprehensive end-to-end integration tests. As your application evolves, tests are automatically updated, maintaining 95%+ coverage without manual overhead.",
      },
      {
        subKey: "pipeline",
        subTitle: "Smart CI/CD",
        point: "Self-Healing Deployment Pipelines",
        description:
          "Our CI/CD pipelines use AI-driven diagnostics to auto-triage deployment failures. If a build breaks, our pipeline analyzes logs, identifies dependency mismatches, and suggests immediate hotfixes.",
      },
    ],
  },
  {
    key: "standards",
    title: "Elite Tech Standards",
    icon: CodeIcon,
    accentColor: "from-blue-500/10 to-indigo-500/10 dark:from-blue-500/20 dark:to-indigo-500/5",
    points: [
      {
        subKey: "perf",
        subTitle: "Lighthouse 98+",
        point: "Uncompromised Core Web Vitals",
        description:
          "We target a 98+ score on mobile and desktop performance metrics. By optimizing bundle size, pre-rendering, lazy-loading assets, and optimizing layouts, we guarantee lightning-fast load times and SEO prioritization.",
      },
      {
        subKey: "typescript",
        subTitle: "Strict Types",
        point: "Type-Safe Solid Architecture",
        description:
          "We strictly configure TypeScript and compiler flags to force total type safety. This eliminates common runtime exceptions, simplifies scaling, and guarantees that codebase refactors are safe and predictable.",
      },
      {
        subKey: "modular",
        subTitle: "Modular UI",
        point: "Compound Reusable Components",
        description:
          "Using clean CSS variables, Tailwind configuration, and compound component patterns (similar to HeroUI), we construct atomic and reusable design systems, ensuring UI visual consistency across your application.",
      },
      {
        subKey: "ssr",
        subTitle: "SSR & Edge",
        point: "Edge-First Next.js & Serverless Hosting",
        description:
          "We configure server-side rendering (SSR), Incremental Static Regeneration (ISR), and Edge-computing endpoints. Your content is rendered at the server edge, closest to users, maximizing speed and efficiency.",
      },
    ],
  },
  {
    key: "security",
    title: "Client Protections",
    icon: SecurityCheckIcon,
    accentColor: "from-violet-500/10 to-purple-500/10 dark:from-violet-500/20 dark:to-purple-500/5",
    points: [
      {
        subKey: "ip",
        subTitle: "100% IP Handover",
        point: "Absolute IP & Asset Ownership Transfer",
        description:
          "From day one, all codebases, design files, domains, API setups, and technical assets are legally registered under your ownership. We deliver complete repository access and sign off on full IP transfers on project handover.",
      },
      {
        subKey: "nda",
        subTitle: "Discovery NDAs",
        point: "Pre-Discovery Non-Disclosure Protection",
        description:
          "We protect your intellectual property and business ideas from the start. We execute legally binding, reciprocal NDAs before any deep-dive architecture discussions or repository access is requested.",
      },
      {
        subKey: "gdpr",
        subTitle: "Privacy First",
        point: "GDPR, HIPAA, and Data Privacy Standards",
        description:
          "We architect systems with user privacy at the core. We implement advanced field-level encryption, OAuth2 secure auth protocols, secure cookie configurations, and GDPR data-deletion flows by default.",
      },
      {
        subKey: "infra",
        subTitle: "Secure Vault",
        point: "Isolated VPC & Key Management Storage",
        description:
          "We deploy web apps in isolated Virtual Private Clouds (VPC) with restricted network configurations. API keys, database credentials, and secrets are locked inside secure vaults (like AWS Secrets Manager/Vercel Keys).",
      },
    ],
  },
  {
    key: "partnership",
    title: "Pod-Based Sprints",
    icon: GitBranchIcon,
    accentColor: "from-amber-500/10 to-orange-500/10 dark:from-amber-500/20 dark:to-orange-500/5",
    points: [
      {
        subKey: "kickoff",
        subTitle: "48h Kickoff",
        point: "Ultra-Fast Onboarding & Setup",
        description:
          "We assemble and align your dedicated engineering pod within 48 hours. By using boilerplate starters and automated workspace generators, we pull code and push our first commit within the first day of onboarding.",
      },
      {
        subKey: "delivery",
        subTitle: "Weekly Sprints",
        point: "Iterative Deliveries & Live Demo Builds",
        description:
          "We manage projects in rapid, weekly sprints. Every Friday, you receive a detailed progress report, a live demo build on a staging environment, and a prioritized plan for the next sprint, maintaining momentum.",
      },
      {
        subKey: "overlap",
        subTitle: "Time-Zone Sync",
        point: "Active Communication & Overlap Hours",
        description:
          "We coordinate schedules to guarantee at least 4 hours of direct working overlap with your local time zone. Our engineers join your Slack, participate in standups, and provide live, synchronous feedback.",
      },
      {
        subKey: "slack",
        subTitle: "Direct Channels",
        point: "Zero Account Managers, Direct Access",
        description:
          "We believe in developer-to-client clarity. You get direct access to the engineers building your product on Slack and Teams, eliminating the delay and misunderstanding caused by traditional account managers.",
      },
    ],
  },
];

// ─── Micro-Visuals for the Sub-Tab Content Panel ────────────────────────────

function SubTabVisual({ subKey }: { subKey: string }) {
  switch (subKey) {
    // AI-Powered Engineering
    case "copilots":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <div className="relative w-18 h-18 flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 rounded-full border border-dashed border-emerald-500/30"
            />
            <motion.div
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-11 h-11 rounded-full bg-emerald-500/10 flex items-center justify-center"
            >
              <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 animate-pulse" />
            </motion.div>
          </div>
        </div>
      );
    case "auditing":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-3 shadow-md w-32"
          >
            <div className="flex items-center gap-1 mb-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-500" />
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
            </div>
            <div className="font-mono text-[9px] text-neutral-500 space-y-1">
              <p className="text-blue-500">const pr = check()</p>
              <p className="text-emerald-500 font-bold">✔ AI Audited</p>
            </div>
          </motion.div>
        </div>
      );
    case "e2e":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            animate={{ scale: [0.9, 1, 0.9] }}
            transition={{ duration: 2.5, repeat: Infinity }}
            className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-600 flex flex-col items-center justify-center text-white shadow-md"
          >
            <span className="text-xs font-black">E2E</span>
            <span className="text-[9px] mt-0.5 opacity-90">100% OK</span>
          </motion.div>
        </div>
      );
    case "pipeline":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center gap-1.5">
          {[1, 2, 3].map((s) => (
            <motion.div
              key={s}
              animate={{
                backgroundColor: ["rgba(16, 185, 129, 0.2)", "rgba(16, 185, 129, 1)", "rgba(16, 185, 129, 0.2)"],
              }}
              transition={{ duration: 1.5, repeat: Infinity, delay: s * 0.3 }}
              className="w-3.5 h-3.5 rounded-full"
            />
          ))}
        </div>
      );

    // Elite Tech Standards
    case "perf":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-18 h-18 rounded-full border-4 border-emerald-500 flex flex-col items-center justify-center text-emerald-500 font-black shadow-inner shadow-emerald-500/10"
          >
            <span className="text-xl leading-none">99</span>
            <span className="text-[7px] uppercase tracking-wider font-extrabold mt-0.5">Perf</span>
          </motion.div>
        </div>
      );
    case "typescript":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            initial={{ y: 5, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="px-4 py-2.5 bg-blue-600/15 border border-blue-500/25 rounded-2xl text-blue-600 dark:text-blue-400 text-xs font-mono font-black"
          >
            strict: true
          </motion.div>
        </div>
      );
    case "modular":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center flex-wrap gap-1.5 max-w-[130px]">
          {["Card", "Tabs", "Button", "Chip"].map((s, idx) => (
            <motion.div
              key={s}
              animate={{ y: [0, idx % 2 === 0 ? -3 : 3, 0] }}
              transition={{ duration: 3, repeat: Infinity, delay: idx * 0.1 }}
              className="px-2 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-[9px] font-black shadow-sm"
            >
              {s}
            </motion.div>
          ))}
        </div>
      );
    case "ssr":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="w-14 h-14 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6 animate-pulse"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9s2.015-9 4.5-9m0 0a9.003 9.003 0 018.716 6.747M12 3a9.003 9.003 0 00-8.716 6.747M3 12h18"
              />
            </svg>
          </motion.div>
        </div>
      );

    // Client Protections
    case "ip":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-250 dark:border-indigo-800 rounded-xl p-3 text-center shadow-sm"
          >
            <span className="text-[8px] text-indigo-500 uppercase tracking-widest font-black block">IP Handover</span>
            <span className="text-[10px] text-neutral-800 dark:text-neutral-200 font-black mt-1 block">100% SECURED</span>
          </motion.div>
        </div>
      );
    case "nda":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            initial={{ rotate: -3 }}
            animate={{ rotate: [ -3, 3, -3 ] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="w-20 h-24 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-2.5 shadow-md flex flex-col justify-between"
          >
            <div className="border-b border-neutral-100 dark:border-neutral-800 pb-1 flex justify-between items-center">
              <span className="text-[8px] font-black uppercase text-neutral-450">NDA</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            </div>
            <div className="space-y-1 my-1">
              <div className="h-1 bg-neutral-100 dark:bg-neutral-800 rounded w-full" />
              <div className="h-1 bg-neutral-100 dark:bg-neutral-800 rounded w-5/6" />
            </div>
            <span className="text-[7px] text-emerald-600 font-bold block text-right font-outfit">SIGNED</span>
          </motion.div>
        </div>
      );
    case "gdpr":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <div className="relative w-14 h-14 flex items-center justify-center">
            <motion.div
              animate={{ rotate: [0, 180, 360] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 border border-indigo-400 rounded-full border-t-transparent"
            />
            <span className="text-[8px] font-black text-indigo-500">GDPR</span>
          </div>
        </div>
      );
    case "infra":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="w-14 h-14 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
              />
            </svg>
          </motion.div>
        </div>
      );

    // Pod-Based Sprints
    case "kickoff":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-3 text-center shadow-sm"
          >
            <span className="text-[8px] text-neutral-450 uppercase tracking-wider font-extrabold block">Kickoff</span>
            <div className="text-base font-black text-blue-600 dark:text-blue-400 mt-0.5">48 Hours</div>
          </motion.div>
        </div>
      );
    case "delivery":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <div className="flex gap-1.5">
            {[1, 2].map((col) => (
              <div key={col} className="w-11 h-16 bg-neutral-100/60 dark:bg-neutral-900/40 rounded-lg p-1 flex flex-col gap-1">
                <span className="w-3 h-1 bg-neutral-250 dark:bg-neutral-800 rounded block" />
                <motion.div
                  animate={{ y: col === 1 ? [0, 6, 0] : [6, 0, 6] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded p-1 shadow-sm"
                >
                  <div className="h-0.5 bg-blue-500 rounded w-full" />
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      );
    case "overlap":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 rounded-full border border-neutral-300 dark:border-neutral-700 flex items-center justify-center relative"
          >
            <div className="w-1 h-1 bg-blue-500 rounded-full" />
            <div className="absolute top-0.5 w-0.5 h-5 bg-neutral-400 dark:bg-neutral-500 origin-bottom" style={{ transform: "rotate(45deg)" }} />
          </motion.div>
        </div>
      );
    case "slack":
      return (
        <div className="relative w-full h-[150px] flex items-center justify-center">
          <div className="flex -space-x-2 select-none">
            {[1, 2].map((s) => (
              <motion.div
                key={s}
                whileHover={{ y: -3 }}
                className={`w-9 h-9 rounded-full border border-white dark:border-neutral-900 bg-gradient-to-tr ${
                  s === 1 ? "from-blue-500 to-indigo-500" : "from-pink-500 to-rose-500"
                } flex items-center justify-center text-[7px] text-white font-bold shadow-md`}
              >
                Dev
              </motion.div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function WhyChoseUs() {
  const [activeTab, setActiveTab] = useState("engineering");
  const activeCategory = WhyChoseUsArray.find((c) => c.key === activeTab) || WhyChoseUsArray[0];

  const [activeSubTab, setActiveSubTab] = useState(activeCategory.points[0].subKey);

  // Sync sub-tab selection when main tab changes
  useEffect(() => {
    setActiveSubTab(activeCategory.points[0].subKey);
  }, [activeTab, activeCategory]);

  return (
    <section className="relative min-h-screen w-full py-24 sm:py-32 px-4 overflow-hidden bg-neutral-50 dark:bg-black transition-colors duration-500">
      
      {/* Background gradients aligned to active tab */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className={`absolute top-[15%] right-[20%] w-[550px] h-[550px] rounded-full blur-[140px] opacity-25 bg-gradient-to-br ${activeCategory.accentColor} transition-all duration-1000`} />
        <div className="absolute bottom-[10%] left-[5%] w-[450px] h-[450px] rounded-full blur-[120px] opacity-15 bg-gradient-to-br from-neutral-200/20 to-transparent dark:from-neutral-800/10" />
      </div>

      {/* Grid Pattern */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: "radial-gradient(circle, #64748b 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="w-full max-w-7xl mx-auto flex flex-col gap-14 sm:gap-20">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mb-4"
          >
            <Chip
              size="sm"
              variant="soft"
              className="bg-neutral-200/40 dark:bg-neutral-900/40 text-[10px] uppercase tracking-widest font-extrabold text-neutral-500 dark:text-neutral-400 px-4 py-1.5 border border-neutral-300/20 dark:border-neutral-800/30 rounded-full"
            >
              Our Value Proposition
            </Chip>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-recoleta text-neutral-900 dark:text-white mb-5"
          >
            Why Choose{" "}
            <span className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 bg-clip-text text-transparent">
              Devebsy
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="text-neutral-500 dark:text-neutral-400 text-base sm:text-lg leading-relaxed font-outfit max-w-2xl"
          >
            We eliminate typical software engineering friction. Get access to vetted talent, save up to 80% on budgets, and work with reliable, agile sprint schedules.
          </motion.p>
        </div>

        {/* Core Layout Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          
          {/* Left Side: Main Category Tabs */}
          <div className="lg:col-span-5 flex flex-col gap-6 select-none z-10">
            <span className="text-[10px] uppercase font-black tracking-widest text-indigo-500 dark:text-indigo-400 text-left px-1">
              Core Benefits
            </span>
            <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-3 lg:pb-0 scrollbar-hidden w-full">
              {WhyChoseUsArray.map((category) => {
                const isActive = activeTab === category.key;
                return (
                  <motion.button
                    key={category.key}
                    onClick={() => setActiveTab(category.key)}
                    whileTap={{ scale: 0.98 }}
                    className={`
                      relative flex items-center gap-3.5 px-5.5 py-4.5 rounded-2xl text-xs sm:text-sm font-black font-outfit
                      transition-colors duration-300 outline-none whitespace-nowrap cursor-pointer shrink-0 lg:shrink
                      ${isActive
                        ? "text-neutral-900 dark:text-neutral-50"
                        : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300"
                      }
                    `}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="active-value-pill"
                        className="absolute inset-0 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200/50 dark:border-neutral-800/40 shadow-sm z-0"
                        transition={{ type: "spring", stiffness: 350, damping: 28 }}
                      />
                    )}

                    <span className="relative z-10 flex items-center justify-center">
                      <HugeiconsIcon
                        icon={category.icon}
                        size={18}
                        className={`transition-colors duration-300 ${isActive ? "text-indigo-500 dark:text-indigo-400" : "text-neutral-600 dark:text-neutral-400"}`}
                      />
                    </span>

                    <span className="relative z-10">{category.title}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Right Side: Sub-Tabs & Detailed Content Card */}
          <div className="lg:col-span-7 flex justify-center items-center">
            <div className="w-full bg-white/5 dark:bg-neutral-900/10 backdrop-blur-md rounded-4xl border border-neutral-200/20 dark:border-neutral-800/30 p-6 sm:p-8 flex flex-col gap-6 shadow-[0_20px_50px_rgba(0,0,0,0.03)] dark:shadow-none relative overflow-hidden min-h-[380px] sm:min-h-[440px]">
              
              {/* Decorative grid lines */}
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
                style={{
                  backgroundImage: "radial-gradient(circle, #64748b 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                }}
              />

              {/* Sub-Tabs Nav */}
              <div className="relative z-10 flex flex-row items-center gap-1 p-1 rounded-2xl bg-neutral-100/50 dark:bg-neutral-900/40 border border-neutral-200/20 dark:border-neutral-800/20 w-full overflow-x-auto scrollbar-hidden select-none">
                {activeCategory.points.map((pt) => {
                  const isSubActive = activeSubTab === pt.subKey;
                  return (
                    <button
                      key={pt.subKey}
                      onClick={() => setActiveSubTab(pt.subKey)}
                      className={`
                        relative flex-1 py-3 px-3.5 rounded-xl text-xs font-black font-outfit text-center 
                        transition-colors duration-300 outline-none whitespace-nowrap cursor-pointer z-10
                        ${isSubActive 
                          ? "text-neutral-900 dark:text-neutral-50" 
                          : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300"
                        }
                      `}
                    >
                      {isSubActive && (
                        <motion.span
                          layoutId="active-subtab-pill"
                          className="absolute inset-0 rounded-xl bg-white dark:bg-neutral-950 border border-neutral-200/40 dark:border-neutral-800/50 shadow-sm z-0"
                          transition={{ type: "spring", stiffness: 350, damping: 28 }}
                        />
                      )}
                      <span className="relative z-10">{pt.subTitle}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Sub-Tab Content with sliding fade transition */}
              <div className="relative flex-1 flex flex-col justify-center">
                <AnimatePresence mode="wait">
                  {activeCategory.points.map((pt) => {
                    if (pt.subKey !== activeSubTab) return null;
                    return (
                      <motion.div
                        key={pt.subKey}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center w-full z-10"
                      >
                        
                        {/* Text Area */}
                        <div className="md:col-span-8 flex flex-col text-start">
                          <span className="text-[10px] uppercase font-black tracking-widest text-indigo-500 dark:text-indigo-400 mb-2 font-outfit">
                            Feature Highlight
                          </span>
                          <h3 className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white font-outfit leading-snug mb-3">
                            {pt.point}
                          </h3>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 font-outfit leading-relaxed">
                            {pt.description}
                          </p>
                        </div>

                        {/* Graphic Area */}
                        <div className="md:col-span-4 flex items-center justify-center border border-neutral-200/20 dark:border-neutral-800/20 rounded-3xl bg-neutral-100/10 dark:bg-neutral-900/5 min-h-[140px] p-2.5">
                          <SubTabVisual subKey={pt.subKey} />
                        </div>

                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
